import {
  B2B_MEMBER_TABLE_EMPTY_CELL,
  getB2bMemberAdminTableSortKey,
  getB2bMemberTableNameAndEmail,
  sortB2bMembersForAdminTable,
  type B2bMemberViewerIdentity,
} from './b2bAccount.helper'
import type { B2bMemberRow } from '../types/b2bAccount.types'
import {
  MEMBERS_DIRECTORY_ROLE_FILTER,
  MEMBERS_DIRECTORY_SORT,
  type MembersDirectoryRoleFilter,
  type MembersDirectorySortOption,
} from '../constants/accountMembersDirectory.constants'

export function normalizeMembersDirectorySearchQuery(raw: string): string {
  return raw.trim().toLowerCase()
}

/** Matches display name or email column only (spec: not username / user id). */
export function b2bMemberMatchesDirectorySearch(
  member: B2bMemberRow,
  normalizedQuery: string,
  viewer: B2bMemberViewerIdentity | null,
): boolean {
  if (!normalizedQuery) return true

  const { nameColumn, emailColumn } = getB2bMemberTableNameAndEmail(member, viewer)
  const parts: string[] = []
  if (nameColumn !== B2B_MEMBER_TABLE_EMPTY_CELL && nameColumn !== 'Member') {
    parts.push(nameColumn)
  }
  if (emailColumn !== B2B_MEMBER_TABLE_EMPTY_CELL) {
    parts.push(emailColumn)
  }

  const haystack = parts.join(' ').toLowerCase()
  return haystack.includes(normalizedQuery)
}

export function filterB2bMembersByDirectoryRole(
  members: B2bMemberRow[],
  roleFilter: MembersDirectoryRoleFilter,
): B2bMemberRow[] {
  if (roleFilter === MEMBERS_DIRECTORY_ROLE_FILTER.ALL) return members
  return members.filter((member) => member.role === roleFilter)
}

function joinedAtMs(iso: string): number {
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : 0
}

export function sortB2bMembersForDirectory(
  members: B2bMemberRow[],
  sortOption: MembersDirectorySortOption,
  viewer: B2bMemberViewerIdentity | null,
): B2bMemberRow[] {
  if (sortOption === MEMBERS_DIRECTORY_SORT.DEFAULT) {
    return sortB2bMembersForAdminTable(members, viewer)
  }

  const sorted = [...members]

  if (sortOption === MEMBERS_DIRECTORY_SORT.NAME_ASC) {
    return sorted.sort((a, b) =>
      getB2bMemberAdminTableSortKey(a, viewer).localeCompare(
        getB2bMemberAdminTableSortKey(b, viewer),
      ),
    )
  }

  if (sortOption === MEMBERS_DIRECTORY_SORT.NAME_DESC) {
    return sorted.sort((a, b) =>
      getB2bMemberAdminTableSortKey(b, viewer).localeCompare(
        getB2bMemberAdminTableSortKey(a, viewer),
      ),
    )
  }

  if (sortOption === MEMBERS_DIRECTORY_SORT.JOINED_NEWEST) {
    return sorted.sort((a, b) => joinedAtMs(b.joinedAt) - joinedAtMs(a.joinedAt))
  }

  if (sortOption === MEMBERS_DIRECTORY_SORT.JOINED_OLDEST) {
    return sorted.sort((a, b) => joinedAtMs(a.joinedAt) - joinedAtMs(b.joinedAt))
  }

  return sortB2bMembersForAdminTable(members, viewer)
}

export interface ApplyMembersDirectoryFiltersInput {
  members: B2bMemberRow[]
  searchQuery: string
  roleFilter: MembersDirectoryRoleFilter
  sortOption: MembersDirectorySortOption
  viewer: B2bMemberViewerIdentity | null
}

/** Filter by role and search, then sort for display in the members table. */
export function applyMembersDirectoryFilters(
  input: ApplyMembersDirectoryFiltersInput,
): B2bMemberRow[] {
  const normalizedQuery = normalizeMembersDirectorySearchQuery(input.searchQuery)
  const afterRole = filterB2bMembersByDirectoryRole(input.members, input.roleFilter)
  const afterSearch = normalizedQuery
    ? afterRole.filter((member) =>
        b2bMemberMatchesDirectorySearch(member, normalizedQuery, input.viewer),
      )
    : afterRole
  return sortB2bMembersForDirectory(afterSearch, input.sortOption, input.viewer)
}

export function shouldShowMembersDirectoryResultsSummary(
  searchQuery: string,
  shownCount: number,
  totalCount: number,
): boolean {
  if (totalCount <= 0) return false
  if (normalizeMembersDirectorySearchQuery(searchQuery).length > 0) return true
  return shownCount !== totalCount
}
